export type PerformanceStatus = 'exceeded' | 'met' | 'near' | 'attention' | 'missing'
export type PerformanceMetric = 'daily' | 'tma' | 'nps' | 'participation'

export type EvaluablePerformanceResult = {
  total_attendances: number
  daily_average: number | null
  tma_seconds: number | null
  nps_score: number | null
  nps_responses: number
  nps_participation_pct: number | null
  target_daily_attendance: number
  target_tma_seconds: number
  target_tma_low_alert_seconds: number
  target_nps: number
  target_nps_participation_pct: number
  benchmark_daily_average: number | null
  benchmark_tma_seconds: number | null
  benchmark_nps_score: number | null
  benchmark_nps_participation_pct: number | null
}

export type MetricEvaluation = {
  metric: PerformanceMetric
  label: string
  value: number | null
  target: number
  achievement: number | null
  status: PerformanceStatus
  benchmark: number | null
  lowTmaAlert?: boolean
}

export function achievementHigherIsBetter(value: number | null, target: number) {
  if (value == null || target <= 0) return null
  return Math.max(0, Math.min(100, value / target * 100))
}

export function achievementLowerIsBetter(value: number | null, target: number) {
  if (value == null || value <= 0 || target <= 0) return null
  return Math.max(0, Math.min(100, target / value * 100))
}

export function participationTargetResponses(result: Pick<EvaluablePerformanceResult, 'total_attendances' | 'target_nps_participation_pct'>) {
  return Math.ceil(Number(result.total_attendances) * Number(result.target_nps_participation_pct) / 100)
}

export function metricStatus(achievement: number | null, exceeded = false): PerformanceStatus {
  if (achievement == null) return 'missing'
  if (achievement >= 100) return exceeded ? 'exceeded' : 'met'
  if (achievement >= 90) return 'near'
  return 'attention'
}

export function evaluateResult(result: EvaluablePerformanceResult): MetricEvaluation[] {
  const dailyAchievement = achievementHigherIsBetter(result.daily_average, Number(result.target_daily_attendance))
  const tmaAchievement = result.tma_seconds != null && result.tma_seconds <= Number(result.target_tma_seconds)
    ? 100
    : achievementLowerIsBetter(result.tma_seconds, Number(result.target_tma_seconds))
  const npsAchievement = achievementHigherIsBetter(result.nps_score, Number(result.target_nps))
  const targetResponses = participationTargetResponses(result)
  const participationAchievement = targetResponses > 0
    ? Math.max(0, Math.min(100, Number(result.nps_responses) / targetResponses * 100))
    : null

  return [
    { metric: 'daily', label: 'Média diária', value: result.daily_average, target: Number(result.target_daily_attendance), achievement: dailyAchievement, status: metricStatus(dailyAchievement, result.daily_average != null && result.daily_average > Number(result.target_daily_attendance)), benchmark: result.benchmark_daily_average },
    { metric: 'tma', label: 'TMA', value: result.tma_seconds, target: Number(result.target_tma_seconds), achievement: tmaAchievement, status: metricStatus(tmaAchievement), benchmark: result.benchmark_tma_seconds, lowTmaAlert: result.tma_seconds != null && result.tma_seconds < Number(result.target_tma_low_alert_seconds) },
    { metric: 'nps', label: 'NPS', value: result.nps_score, target: Number(result.target_nps), achievement: npsAchievement, status: metricStatus(npsAchievement, result.nps_score != null && result.nps_score > Number(result.target_nps)), benchmark: result.benchmark_nps_score },
    { metric: 'participation', label: 'Participação NPS', value: result.nps_participation_pct, target: Number(result.target_nps_participation_pct), achievement: participationAchievement, status: metricStatus(participationAchievement, result.nps_participation_pct != null && result.nps_participation_pct > Number(result.target_nps_participation_pct)), benchmark: result.benchmark_nps_participation_pct },
  ]
}

export function strongestMetric(evaluations: MetricEvaluation[]) {
  return evaluations.filter((item) => item.achievement != null).sort((a, b) => (b.achievement ?? -1) - (a.achievement ?? -1))[0] ?? null
}

export function attentionMetric(evaluations: MetricEvaluation[]) {
  return evaluations.filter((item) => item.achievement != null && item.achievement < 100).sort((a, b) => (a.achievement ?? 101) - (b.achievement ?? 101))[0] ?? null
}
